import { useState, useEffect } from "react";
import {
  TextField,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Button,
  Divider,
  Alert,
  Typography,
  InputAdornment,
  Snackbar,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import VerifiedIcon from "@mui/icons-material/Verified";
import SendIcon from "@mui/icons-material/Send";
import { FaExternalLinkAlt } from "react-icons/fa";
import AdminApiModule from "../../components/AdminApi/Api/api";
import CenterRegistrationApi from "../../components/centerRegistration/Api/api";
import { IndianStates } from "../../assets/IndianStates";
import CopyButton from "../../components/ServicesStatus/ClipbaordButton";
import moment from "moment";
import {
  canViewTickets,
  canUpdateTicket,
  canViewContactNumber,
  hasFullAccess,
} from "../../utility/permissionUtils";

const AVAILABLE_TIME_PREFERENCES = [
  "IMMEDIATELY",
  "WITHIN_24_HOURS",
  "SPECIFIC_DATE_AND_TIME",
];

const AddTicket = ({ reduxState, globalState }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get user permissions from redux state
  const userPermissions = reduxState?.adminDetails?.role_id?.permissions;

  const [formData, setFormData] = useState({
    serviceRequired: "",
    specificRequirement: "",
    name: "",
    phoneNumber: "",
    alternatePhoneNumber: "",
    houseNumber: "",
    locality: "",
    city: "",
    state: "",
    country: "INDIA",
    pincode: "",
    timePreference: "",
    authorizedForClient: "",
    offerCode: "ONIT2026",
    is_paid: true,
    closing_ticket_price: "",
    admin_remarks: "",
    specific_date_time: new Date(),
    service_provided_client_id: "",
    service_provided_due_amount: "0",
    serial_number: "",
  });

  const [state, setState] = useState({
    isFormValid: false,
    servicesList: [],
    clientsList: [],
    isRegistering: false,
    isEditDetailsExists: false,
    doesTicketExists: false,
    TicketModalOpen: false,
    TicketModalData: [],
    toast: {
      open: false,
      msg: "",
      duration: 5000,
      type: "",
    },
  });

  useEffect(() => {
    getAllServicesExists();
    getAllClients();

    const editTicketId = new URLSearchParams(location.search).get("edit");
    if (editTicketId != null) {
      setState((prev) => ({ ...prev, isEditDetailsExists: true }));
      getTicketDetails(editTicketId);
    }
  }, [location.search]);

  const getAllServicesExists = () => {
    new CenterRegistrationApi()
      .getAllActiveServices()
      .then((res) => {
        setState((prev) => ({ ...prev, servicesList: res.data }));
      })
      .catch(console.error);
  };

  const getAllClients = () => {
    new AdminApiModule()
      .getAllClients()
      .then((res) => {
        const parsedData = res?.data?.length > 0 ? res.data : [];
        setState((prev) => ({ ...prev, clientsList: parsedData }));
      })
      .catch(console.error);
  };

  const getTicketDetails = (editTicketId) => {
    const payload = { ticket_obj_id: editTicketId };

    new AdminApiModule()
      .getAllTickets(payload)
      .then((res) => {
        if (res?.data?.length) {
          setState((prev) => ({ ...prev, doesTicketExists: true }));
          updateTicketDetails(res?.data?.[0]);
        }
      })
      .catch(console.error);
  };

  const updateTicketDetails = (ticketDetails) => {
    setFormData({
      serviceRequired: ticketDetails?.service_provided_for?._id,
      specificRequirement: ticketDetails?.specific_requirement,
      name: ticketDetails?.personal_details?.name,
      phoneNumber:
        ticketDetails?.personal_details?.primary_phone?.mobile_number,
      alternatePhoneNumber:
        ticketDetails?.personal_details?.alternate_phone?.mobile_number,
      houseNumber: ticketDetails?.address_details?.house_number,
      locality: ticketDetails?.address_details?.locality,
      city: ticketDetails?.address_details?.city,
      state: ticketDetails?.address_details?.state,
      country: "INDIA",
      pincode: ticketDetails?.address_details?.pincode,
      timePreference: ticketDetails?.time_preference?.time_preference_type,
      authorizedForClient: ticketDetails?.authorized_client_id?._id,
      offerCode: ticketDetails?.offers_applied?.offer_code || "ONIT2026",
      is_paid: ticketDetails?.admin_setting?.is_paid,
      closing_ticket_price: ticketDetails?.closing_ticket_price,
      admin_remarks: ticketDetails?.remarks?.admin_remarks,
      specific_date_time: ticketDetails?.time_preference?.specific_date_time,
      service_provided_client_id:
        ticketDetails?.service_provided_client_id || "",
      service_provided_due_amount:
        ticketDetails?.service_provided_due_amount || "0",
      serial_number: ticketDetails?.serial_number || "", // Added serial number field
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "authorizedForClient") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        service_provided_client_id: "",
        service_provided_due_amount: "0",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateTimeChange = (newValue) => {
    setFormData((prev) => ({ ...prev, specific_date_time: newValue }));
  };

  const showDialog = (open, type, msg) => {
    setState((prev) => ({
      ...prev,
      toast: {
        open,
        msg,
        duration: 5000,
        type,
      },
    }));
  };

  const closeDialog = () => {
    setState((prev) => ({
      ...prev,
      toast: {
        open: false,
        msg: "",
        duration: 5000,
        type: "",
      },
    }));
  };

  const handleRegister = () => {
    const {
      serviceRequired,
      name,
      phoneNumber,
      houseNumber,
      pincode,
      timePreference,
      offerCode,
    } = formData;

    if (
      !(
        serviceRequired &&
        name &&
        phoneNumber &&
        houseNumber &&
        pincode &&
        timePreference &&
        offerCode
      )
    ) {
      setState((prev) => ({ ...prev, isFormValid: true }));
      showDialog(true, "error", "Please fill in all required details");
      return;
    }

    if (
      formData.timePreference === "SPECIFIC_DATE_AND_TIME" &&
      !formData.specific_date_time
    ) {
      showDialog(true, "error", "Please fill in Specific date and time");
      return;
    }

    setState((prev) => ({ ...prev, isRegistering: true }));

    const payload = {
      personal_details: {
        primary_phone: {
          country_code: "+91",
          mobile_number: formData.phoneNumber,
        },
        alternate_phone: {
          country_code: formData.alternatePhoneNumber.length > 0 ? "+91" : "",
          mobile_number: formData.alternatePhoneNumber,
        },
        name: formData.name,
      },
      specific_requirement: formData.specificRequirement,
      service_provided_for: formData.serviceRequired,
      address_details: {
        house_number: formData.houseNumber,
        locality: formData.locality,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        country: formData.country,
      },
      time_preference: {
        time_preference_type: formData.timePreference,
        specific_date_time: formData.specific_date_time,
      },
      offers_applied: {
        offer_code: formData.offerCode,
      },
      create_name: globalState?.adminDetails?.user_name,
    };

    // Add serial number to payload if it exists
    if (formData.serial_number) {
      payload.serial_number = formData.serial_number;
    }

    if (formData.authorizedForClient) {
      payload.authorized_client_id = formData.authorizedForClient;
      payload.service_provided_client_id = formData.service_provided_client_id;
      payload.service_provided_due_amount =
        formData.service_provided_due_amount;
    }

    if (state.isEditDetailsExists) {
      // Check if user has permission to update tickets
      if (
        !canUpdateTicket(userPermissions) &&
        !hasFullAccess(userPermissions)
      ) {
        showDialog(
          true,
          "error",
          "You don't have permission to update tickets"
        );
        setState((prev) => ({ ...prev, isRegistering: false }));
        return;
      }

      payload.ticket_obj_id = new URLSearchParams(location.search).get("edit");
      payload.closing_ticket_price = formData.closing_ticket_price || "";
      payload.admin_remarks = formData.admin_remarks || "";
      delete payload.is_paid;
      delete payload.service_provided_client_id;

      new AdminApiModule()
        .updateTicket(payload)
        .then(() => {
          showDialog(true, "success", "Ticket updated successfully");
          setState((prev) => ({ ...prev, isRegistering: false }));
          navigate("/Tickets");
        })
        .catch((err) => {
          console.error(err);
          showDialog(true, "error", err?.response?.data?.message);
          setState((prev) => ({ ...prev, isRegistering: false }));
        });
      return;
    }

    // For new ticket creation - check if user has permission to add tickets
    if (!canViewTickets(userPermissions) && !hasFullAccess(userPermissions)) {
      showDialog(true, "error", "You don't have permission to add tickets");
      setState((prev) => ({ ...prev, isRegistering: false }));
      return;
    }

    new AdminApiModule()
      .createTicket(payload)
      .then((res) => {
        const TicketData = res?.data;
        showDialog(true, "success", "Ticket Created successfully");
        setState((prev) => ({ ...prev, TicketModalData: TicketData }));

        // Reset form
        setFormData({
          serviceRequired: "",
          specificRequirement: "",
          name: "",
          phoneNumber: "",
          alternatePhoneNumber: "",
          houseNumber: "",
          locality: "",
          city: "",
          state: "",
          country: "INDIA",
          pincode: "",
          timePreference: "",
          offerCode: "ONIT2026",
          authorizedForClient: "",
          is_paid: true,
          closing_ticket_price: "",
          admin_remarks: "",
          specific_date_time: new Date(),
          service_provided_client_id: "",
          service_provided_due_amount: "0",
          serial_number: "", // Reset serial number field
        });

        setTimeout(() => {
          showDialog(true, "info", "Add more ticket");
          setState((prev) => ({ ...prev, TicketModalOpen: true }));
        }, 2000);
      })
      .catch((err) => {
        console.error(err);
        setState((prev) => ({ ...prev, isRegistering: false }));
        showDialog(true, "error", err?.response?.data?.message);
      });
  };

  const openWhatsapp = (number, id, name) => {
    if (number) {
      const formattedPhoneNumber = number.replace(/[^\d]/g, "");
      const a = `https://app.onit.services/#/ServiceStatus/?${id}`;
      const message = `Dear ${name} request Onit has been created, Please click on the link to check status: ${a} Download the App from playstore and get more Benefits and Offers`;
      const encodedMessage = encodeURIComponent(message);
      window.open(
        `https://web.whatsapp.com/send?phone=${formattedPhoneNumber}&text=${encodedMessage}`,
        "_blank"
      );
    }
  };

  const handleModalClose = () => {
    setState((prev) => ({ ...prev, TicketModalOpen: false }));
  };

  const getPincodeFromLocation = () => {
    // Implement your pincode fetch logic here
    console.log("Fetching pincode from location");
  };

  // Enhanced permission checks using utility functions
  const canAddTicket =
    canViewTickets(userPermissions) || hasFullAccess(userPermissions);
  const canEditTicket =
    canUpdateTicket(userPermissions) || hasFullAccess(userPermissions);
  const canViewContactNumbers =
    canViewContactNumber(userPermissions) || hasFullAccess(userPermissions);

  // Permission checks
  if (state.isEditDetailsExists && !state.doesTicketExists) {
    return <div className="p-4 text-red-500">Page not found!</div>;
  }

  if (state.isEditDetailsExists && !canEditTicket) {
    return (
      <div className="p-4 text-red-500">
        No permission to update ticket, contact admin
      </div>
    );
  }

  if (!state.isEditDetailsExists && !canAddTicket) {
    return (
      <div className="p-4 text-red-500">
        No permission to add ticket, contact admin
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div className="max-w-8xl mx-auto px-4 pb-8">
        {/* Success Modal */}
        <Dialog
          open={state.TicketModalOpen}
          onClose={handleModalClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            id="alert-dialog-title"
            className="flex items-center justify-center"
          >
            <VerifiedIcon color="success" fontSize="large" className="mr-2" />
            <Typography variant="h6" className="text-center">
              Request Created Successfully!
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <Typography variant="body1" className="font-medium">
                    Ticket ID:
                  </Typography>
                  <Typography variant="body1">
                    {state.TicketModalData?.ticket_id}
                  </Typography>
                </div>
                <Divider />
                <div className="flex justify-between">
                  <Typography variant="body1" className="font-medium">
                    Status:
                  </Typography>
                  <Typography variant="body1">
                    {state.TicketModalData?.ticket_status}
                  </Typography>
                </div>
                <Divider />
                <div className="flex justify-between">
                  <Typography variant="body1" className="font-medium">
                    Created At:
                  </Typography>
                  <Typography variant="body1">
                    {moment(state.TicketModalData?.createdAt).format(
                      "DD MMM YYYY, hh:mm A"
                    )}
                  </Typography>
                </div>
                <Divider />
                <div className="flex justify-between items-center">
                  <Typography variant="body1" className="font-medium">
                    Tracking Link:
                  </Typography>
                  <div className="flex items-center space-x-2">
                    <CopyButton
                      webLink={`https://app.onit.services/#/ServiceStatus/?${state.TicketModalData?._id}`}
                    />
                    <Link
                      to={`/ServiceStatus/?${state.TicketModalData?._id}`}
                      className="flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <FaExternalLinkAlt className="mr-1" />
                      <Typography variant="caption">Open</Typography>
                    </Link>
                  </div>
                </div>
              </div>
            </DialogContentText>
          </DialogContent>
          <DialogActions className="justify-center">
            <Button
              onClick={handleModalClose}
              variant="contained"
              color="primary"
              autoFocus
              className="w-full"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Notification */}
        <Snackbar
          open={state.toast.open}
          autoHideDuration={state.toast.duration}
          onClose={closeDialog}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={closeDialog}
            severity={state.toast.type === "error" ? "error" : "success"}
            sx={{ width: "100%" }}
          >
            {state.toast.msg}
          </Alert>
        </Snackbar>

        {/* Main Form */}
        <Typography variant="h4" className="font-bold mb-6 text-center">
          {state.isEditDetailsExists ? "Update Request" : "Add New Request"}
        </Typography>

        <Card className="rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Service Required */}
            <FormControl fullWidth>
              <InputLabel id="service-required-label">
                Service You Required *
              </InputLabel>
              <Select
                labelId="service-required-label"
                id="serviceRequired"
                name="serviceRequired"
                value={formData.serviceRequired}
                onChange={handleChange}
                label="Service You Required *"
                error={state.isFormValid && !formData.serviceRequired}
              >
                <MenuItem value="">
                  <em>Select a service</em>
                </MenuItem>
                {state.servicesList?.map((service) => (
                  <MenuItem key={service._id} value={service._id}>
                    {service.service_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Specific Requirement */}
            <TextField
              fullWidth
              label="Specific Requirement"
              name="specificRequirement"
              value={formData.specificRequirement}
              onChange={handleChange}
              multiline
              rows={2}
            />

            {/* Name */}
            <TextField
              fullWidth
              label="Name (Contact Person) *"
              name="name"
              value={formData.name}
              onChange={handleChange}
              error={state.isFormValid && !formData.name}
              helperText={state.isFormValid && !formData.name ? "Required" : ""}
            />

            {/* Phone Number */}
            <TextField
              fullWidth
              label="Phone Number *"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              error={state.isFormValid && !formData.phoneNumber}
              helperText={
                state.isFormValid && !formData.phoneNumber ? "Required" : ""
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
              inputProps={{
                maxLength: 10,
              }}
            />

            {/* Alternate Phone Number */}
            <TextField
              fullWidth
              label="Alternate Phone Number"
              name="alternatePhoneNumber"
              value={formData.alternatePhoneNumber}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">+91</InputAdornment>
                ),
              }}
              inputProps={{
                maxLength: 10,
              }}
            />

            {/* House Number */}
            <TextField
              fullWidth
              label="House Number / Street *"
              name="houseNumber"
              value={formData.houseNumber}
              onChange={handleChange}
              error={state.isFormValid && !formData.houseNumber}
              helperText={
                state.isFormValid && !formData.houseNumber ? "Required" : ""
              }
            />

            {/* Locality */}
            <TextField
              fullWidth
              label="Locality *"
              name="locality"
              value={formData.locality}
              onChange={handleChange}
              error={state.isFormValid && !formData.locality}
              helperText={
                state.isFormValid && !formData.locality ? "Required" : ""
              }
            />

            {/* City */}
            <TextField
              fullWidth
              label="City *"
              name="city"
              value={formData.city}
              onChange={handleChange}
              error={state.isFormValid && !formData.city}
              helperText={state.isFormValid && !formData.city ? "Required" : ""}
            />

            {/* State */}
            <FormControl fullWidth>
              <InputLabel id="state-label">State *</InputLabel>
              <Select
                labelId="state-label"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                label="State *"
                error={state.isFormValid && !formData.state}
              >
                <MenuItem value="">
                  <em>Select state</em>
                </MenuItem>
                {IndianStates?.map((state) => (
                  <MenuItem
                    key={state.value}
                    className="capitalize"
                    value={state.value}
                  >
                    {state.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Country */}
            <TextField
              fullWidth
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              disabled
            />

            {/* Pincode */}
            <FormControl fullWidth>
              <TextField
                label="Pincode *"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                error={state.isFormValid && !formData.pincode}
                helperText={
                  state.isFormValid && !formData.pincode ? "Required" : ""
                }
                inputProps={{
                  maxLength: 6,
                }}
              />
            </FormControl>

            {/* Time Preference */}
            <FormControl fullWidth>
              <InputLabel id="time-preference-label">
                Time Preference *
              </InputLabel>
              <Select
                labelId="time-preference-label"
                id="timePreference"
                name="timePreference"
                value={formData.timePreference}
                onChange={handleChange}
                label="Time Preference *"
                error={state.isFormValid && !formData.timePreference}
              >
                <MenuItem value="">
                  <em>Select time preference</em>
                </MenuItem>
                {AVAILABLE_TIME_PREFERENCES?.map((pref) => (
                  <MenuItem key={pref} value={pref}>
                    {pref.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Specific Date and Time */}
            {formData.timePreference === "SPECIFIC_DATE_AND_TIME" && (
              <div className="col-span-1 md:col-span-2">
                <DateTimePicker
                  label="Select Date and Time *"
                  value={formData.specific_date_time}
                  onChange={handleDateTimeChange}
                  minDate={new Date()}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth helperText="" />
                  )}
                />
              </div>
            )}

            {/* Offer Code */}
            <TextField
              fullWidth
              label="Offer Code *"
              name="offerCode"
              value={formData.offerCode}
              onChange={handleChange}
              error={state.isFormValid && !formData.offerCode}
              helperText={
                state.isFormValid && !formData.offerCode ? "Required" : ""
              }
            />

            {/* Serial Number */}
            <TextField
              fullWidth
              label="Serial Number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              placeholder="Enter product serial number"
            />

            {/* Client Authorization */}
            {!state.isEditDetailsExists && (
              <>
                <FormControl fullWidth>
                  <InputLabel id="client-auth-label">
                    Authorize for client (optional)
                  </InputLabel>
                  <Select
                    labelId="client-auth-label"
                    id="authorizedForClient"
                    name="authorizedForClient"
                    value={formData.authorizedForClient}
                    onChange={handleChange}
                    label="Authorize for client (optional)"
                  >
                    <MenuItem value="">
                      <em>Select client</em>
                    </MenuItem>
                    {state.clientsList?.map((client) => (
                      <MenuItem key={client._id} value={client._id}>
                        {client.client_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {formData.authorizedForClient && (
                  <>
                    <TextField
                      fullWidth
                      label="Client ID"
                      name="service_provided_client_id"
                      value={formData.service_provided_client_id}
                      onChange={handleChange}
                    />

                    <TextField
                      fullWidth
                      label="DUE Amount"
                      name="service_provided_due_amount"
                      type="number"
                      value={formData.service_provided_due_amount}
                      onChange={handleChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </>
                )}
              </>
            )}

            {/* Admin Fields for Edit Mode */}
            {state.isEditDetailsExists && (
              <>
                <TextField
                  fullWidth
                  label="Closing price"
                  name="closing_ticket_price"
                  type="number"
                  value={formData.closing_ticket_price}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">₹</InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Admin remarks"
                  name="admin_remarks"
                  value={formData.admin_remarks}
                  onChange={handleChange}
                />
              </>
            )}

            {/* Submit Button */}
            <div className="col-span-1 md:col-span-2 flex justify-center mt-4">
              <Button
                onClick={handleRegister}
                disabled={state.isRegistering}
                variant="contained"
                color="primary"
                size="large"
                endIcon={
                  state.isRegistering ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon />
                  )
                }
                className="w-full md:w-auto"
              >
                {state.isRegistering
                  ? "Processing..."
                  : state.isEditDetailsExists
                  ? "Update Request"
                  : "Submit Request"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </LocalizationProvider>
  );
};

const mapStateToProps = (state) => ({
  reduxState: state.userReducer,
  globalState: state.AdminReducer,
});

export default connect(mapStateToProps)(AddTicket);
